import React from 'react';
import PropTypes from 'prop-types';
import Isvg from 'react-inlinesvg';
import { useIntl } from 'react-intl';

import {
  Combobox,
  ComboboxEmpty,
  ComboboxFooter,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from 'components/Combobox';
import { Label } from 'components/HookForm';
import Message from 'components/Message';
import { SvgArrow } from 'components/Svgs';
import When from 'components/When';
import { ICONS } from 'constants/icons';
import { visibleModifiers } from 'features/Sku/Modifiers/Modifiers.formState';
import { bem } from 'helpers/class-name';

import {
  SKU_FLYOUT_DROPDOWN_MIN_MENU_HEIGHT,
  SKU_FLYOUT_DROPDOWN_VIEWPORT_MARGIN,
  SKU_FLYOUT_FOOTER_OFFSET,
} from '../SkuItems.constants';

import messages from './SkuModifierGroupPicker.messages';
import './SkuModifierGroupPicker.scss';

const { block, element } = bem('sku-modifier-group-picker');

const getGroupId = group => group?.core?.id;
const getGroupName = group => group?.core?.name || '';
const getGroupDetail = group =>
  visibleModifiers(group)
    .map(modifier => modifier.core?.name)
    .filter(Boolean)
    .join(', ');

const SkuModifierGroupPicker = ({ groups, value, onChange, onAddModifierGroup }) => {
  const intl = useIntl();

  const selectedIds = Array.isArray(value) ? value : [];
  const selectedGroups = selectedIds
    .map(groupId => groups.find(group => getGroupId(group) === groupId))
    .filter(Boolean);

  const emptyMessage = groups.length === 0 ? messages.noGroups : messages.noResults;

  return (
    <div className={block()}>
      <Label message={messages.label} optional />
      <Combobox
        value={selectedIds}
        onChange={onChange}
        multiple
        menuBottomOffset={SKU_FLYOUT_FOOTER_OFFSET}
        menuViewportMargin={SKU_FLYOUT_DROPDOWN_VIEWPORT_MARGIN}
        menuMinHeight={SKU_FLYOUT_DROPDOWN_MIN_MENU_HEIGHT}>
        <ComboboxInput
          placeholder={intl.formatMessage(messages.searchPlaceholder)}
          prefix={<Isvg src={ICONS.searchGlassOutline} className={element('search-icon')} />}
          suffix={<SvgArrow direction="down" />}
        />
        <ComboboxList>
          {groups.map(group => {
            const groupId = getGroupId(group);
            return (
              <ComboboxItem key={groupId} value={groupId} label={getGroupName(group)}>
                <div className={element('option')}>
                  <div className={element('option-text')}>
                    <span className={element('option-name')}>{getGroupName(group)}</span>
                    <span className={element('option-detail')}>{getGroupDetail(group)}</span>
                  </div>
                  <When predicate={selectedIds.includes(groupId)}>
                    <Isvg src={ICONS.checkmark} className={element('option-check')} />
                  </When>
                </div>
              </ComboboxItem>
            );
          })}
          {onAddModifierGroup && (
            <ComboboxFooter>
              <button type="button" className={element('add-group-option')} onClick={onAddModifierGroup}>
                <Isvg src={ICONS.plus} className={element('add-group-icon')} />
                <Message message={messages.addModifierGroup} />
              </button>
            </ComboboxFooter>
          )}
        </ComboboxList>
        <ComboboxEmpty>
          <Message message={emptyMessage} />
        </ComboboxEmpty>
      </Combobox>
      <When predicate={selectedGroups.length > 0}>
        <ul className={element('selection')}>
          {selectedGroups.map(group => {
            const groupId = getGroupId(group);
            const groupName = getGroupName(group);
            return (
              <li key={groupId} className={element('selection-row')}>
                <div className={element('selection-text')}>
                  <span className={element('selection-name')}>{groupName}</span>
                  <span className={element('selection-detail')}>{getGroupDetail(group)}</span>
                </div>
                <button
                  type="button"
                  className={element('selection-remove')}
                  aria-label={intl.formatMessage(messages.removeGroupAriaLabel, { groupName })}
                  onClick={() => onChange(selectedIds.filter(id => id !== groupId))}>
                  <Message message={messages.removeGroup} />
                </button>
              </li>
            );
          })}
        </ul>
      </When>
    </div>
  );
};

SkuModifierGroupPicker.propTypes = {
  groups: PropTypes.arrayOf(
    PropTypes.shape({
      core: PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
      }),
    })
  ),
  value: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func.isRequired,
  onAddModifierGroup: PropTypes.func,
};

SkuModifierGroupPicker.defaultProps = {
  groups: [],
  value: [],
  onAddModifierGroup: null,
};

export default SkuModifierGroupPicker;
